import { useState, useEffect } from 'react'
import { quizService } from '../../services/quizService'
import toast from 'react-hot-toast'
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  TrophyIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

export default function ModuleQuiz({ moduleId, onComplete }) {
  const [loading, setLoading] = useState(true)
  const [questions, setQuestions] = useState([])
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [attempt, setAttempt] = useState(null)
  const [answers, setAnswers] = useState({})
  const [feedback, setFeedback] = useState({})
  const [quizCompleted, setQuizCompleted] = useState(false)
  const [results, setResults] = useState(null)
  const [userAnswer, setUserAnswer] = useState('')
  const [submittingAnswer, setSubmittingAnswer] = useState(false)

  useEffect(() => {
    loadQuizData()
  }, [moduleId])

  const loadQuizData = async () => {
    try {
      setLoading(true)
      const data = await quizService.getQuestions(moduleId)
      setQuestions(data.questions || [])
      
      // Check if user has a completed attempt
      if (data.latest_attempt && data.latest_attempt.status === 'completed') {
        setQuizCompleted(true)
        const resultsData = await quizService.getResults(data.latest_attempt.id)
        setResults(resultsData)
      }
    } catch (error) {
      console.error('Failed to load quiz:', error)
      toast.error('Failed to load quiz questions')
    } finally {
      setLoading(false)
    }
  }

  const startQuiz = async () => {
    try {
      const data = await quizService.startAttempt(moduleId)
      setAttempt(data.attempt)
      setCurrentQuestionIndex(0)
      setAnswers({})
      setFeedback({})
      setQuizCompleted(false)
      setResults(null)
      toast.success('Quiz started! Good luck!')
    } catch (error) {
      console.error('Failed to start quiz:', error)
      toast.error('Failed to start quiz')
    }
  }

  const submitAnswer = async () => {
    if (!userAnswer.trim()) {
      toast.error('Please provide an answer')
      return
    }

    const currentQuestion = questions[currentQuestionIndex]
    
    try {
      setSubmittingAnswer(true)
      const response = await quizService.submitAnswer(
        attempt.id,
        currentQuestion.id,
        userAnswer
      )

      // Store answer and feedback
      setAnswers(prev => ({
        ...prev,
        [currentQuestion.id]: {
          userAnswer,
          isCorrect: response.is_correct,
          pointsEarned: response.points_earned,
        }
      }))

      setFeedback({
        isCorrect: response.is_correct,
        explanation: response.explanation,
        correctAnswer: response.correct_answer,
        pointsEarned: response.points_earned,
      })

      if (response.is_correct) {
        toast.success(`Correct! +${response.points_earned} points`, {
          icon: '🎉',
        })
      } else {
        toast.error('Incorrect answer', {
          icon: '❌',
        })
      }
    } catch (error) {
      console.error('Failed to submit answer:', error)
      if (error.response?.data?.error) {
        toast.error(error.response.data.error)
      } else {
        toast.error('Failed to submit answer')
      }
    } finally {
      setSubmittingAnswer(false)
    }
  }

  const nextQuestion = () => {
    setCurrentQuestionIndex(prev => prev + 1)
    setUserAnswer('')
    setFeedback({})
  }

  const completeQuiz = async () => {
    try {
      const data = await quizService.completeAttempt(attempt.id)
      
      // Fetch full results with question details
      const resultsData = await quizService.getResults(attempt.id)
      setResults(resultsData)
      setQuizCompleted(true)
      
      if (data.passed) {
        toast.success('Congratulations! You passed!', {
          icon: '🎉',
          duration: 4000,
        })
      } else {
        toast.error('You did not pass. Try again!', {
          duration: 4000,
        })
      }

      if (onComplete) {
        onComplete(data)
      }
    } catch (error) {
      console.error('Failed to complete quiz:', error)
      toast.error('Failed to complete quiz')
    }
  }

  const retryQuiz = () => {
    startQuiz()
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <div className="text-gray-600">Loading quiz...</div>
        </div>
      </div>
    )
  }

  if (questions.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <p className="text-gray-600">No quiz questions available for this module yet.</p>
        </div>
      </div>
    )
  }

  // Show results if quiz is completed
  if (quizCompleted && results) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${
            results.passed ? 'bg-green-100' : 'bg-red-100'
          }`}>
            {results.passed ? (
              <TrophyIcon className="w-10 h-10 text-green-600" />
            ) : (
              <XCircleIcon className="w-10 h-10 text-red-600" />
            )}
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {results.passed ? 'Quiz Passed!' : 'Quiz Not Passed'}
          </h2>
          
          <div className="text-4xl font-bold text-cte-blue mb-4">
            {Number(results.attempt.percentage).toFixed(1)}%
          </div>

          <div className="flex items-center justify-center space-x-8 text-sm text-gray-600 mb-6">
            <div>
              <span className="font-semibold text-gray-900">{results.attempt.score}</span>
              <span className="text-gray-500"> / {results.attempt.max_score} points</span>
            </div>
            <div>
              <span className="font-semibold text-gray-900">{results.attempt.correct_answers}</span>
              <span className="text-gray-500"> / {results.attempt.total_questions} correct</span>
            </div>
          </div>

          {!results.passed && (
            <p className="text-gray-600 mb-6">
              You need 70% or higher to pass. Keep learning and try again!
            </p>
          )}
        </div>

        {/* Detailed Results */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Review Your Answers</h3>
          <div className="space-y-4">
            {results.answers.map((answer, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border-2 ${
                  answer.is_correct
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-start space-x-3 mb-2">
                  {answer.is_correct ? (
                    <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 mb-2">{answer.question}</p>
                    <div className="text-sm space-y-1">
                      <p>
                        <span className="font-medium">Your answer:</span>{' '}
                        <span className={answer.is_correct ? 'text-green-700' : 'text-red-700'}>
                          {answer.user_answer}
                        </span>
                      </p>
                      {!answer.is_correct && (
                        <p>
                          <span className="font-medium">Correct answer:</span>{' '}
                          <span className="text-green-700">{answer.correct_answer}</span>
                        </p>
                      )}
                      {answer.explanation && (
                        <p className="text-gray-600 mt-2">
                          <span className="font-medium">Explanation:</span> {answer.explanation}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-sm font-semibold">
                    +{answer.points_earned}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Retry Button */}
        <div className="mt-8 text-center">
          <button
            onClick={retryQuiz}
            className="inline-flex items-center px-6 py-3 bg-cte-blue text-white font-semibold rounded-lg hover:bg-cte-blue-dark transition-colors"
          >
            <ArrowPathIcon className="w-5 h-5 mr-2" />
            Retake Quiz
          </button>
        </div>
      </div>
    )
  }

  // Show start screen if no attempt
  if (!attempt) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4">
            <ClockIcon className="w-10 h-10 text-cte-blue" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Module Quiz</h2>
          <p className="text-gray-600 mb-6">
            Test your knowledge with {questions.length} questions
          </p>

          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left max-w-md mx-auto">
            <h3 className="font-semibold text-gray-900 mb-2">Quiz Information:</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• {questions.length} questions total</li>
              <li>• Answer all questions to complete</li>
              <li>• Immediate feedback on each answer</li>
              <li>• 70% required to pass</li>
              <li>• You can retake as many times as needed</li>
            </ul>
          </div>

          <button
            onClick={startQuiz}
            className="px-8 py-3 bg-cte-blue text-white font-semibold rounded-lg hover:bg-cte-blue-dark transition-colors"
          >
            Start Quiz
          </button>
        </div>
      </div>
    )
  }

  // Show current question
  const currentQuestion = questions[currentQuestionIndex]
  const isLastQuestion = currentQuestionIndex === questions.length - 1
  const hasAnsweredCurrent = answers[currentQuestion.id]

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
      {/* Progress Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-600">
            Question {currentQuestionIndex + 1} of {questions.length}
          </span>
          <span className="text-sm font-medium text-cte-blue">
            {currentQuestion.points} points
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-cte-blue h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          {currentQuestion.question_text}
        </h3>

        {/* Answer Input based on question type */}
        {currentQuestion.type === 'multiple_choice' && (
          <div className="space-y-3">
            {currentQuestion.options.map((option, index) => (
              <label
                key={index}
                className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  userAnswer === option
                    ? 'border-cte-blue bg-blue-50'
                    : 'border-gray-200 hover:border-cte-blue hover:bg-gray-50'
                } ${hasAnsweredCurrent ? 'pointer-events-none opacity-60' : ''}`}
              >
                <input
                  type="radio"
                  name="answer"
                  value={option}
                  checked={userAnswer === option}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  disabled={hasAnsweredCurrent}
                  className="w-4 h-4 text-cte-blue"
                />
                <span className="ml-3 text-gray-900">{option}</span>
              </label>
            ))}
          </div>
        )}

        {currentQuestion.type === 'true_false' && (
          <div className="space-y-3">
            {['True', 'False'].map((option) => (
              <label
                key={option}
                className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  userAnswer === option
                    ? 'border-cte-blue bg-blue-50'
                    : 'border-gray-200 hover:border-cte-blue hover:bg-gray-50'
                } ${hasAnsweredCurrent ? 'pointer-events-none opacity-60' : ''}`}
              >
                <input
                  type="radio"
                  name="answer"
                  value={option}
                  checked={userAnswer === option}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  disabled={hasAnsweredCurrent}
                  className="w-4 h-4 text-cte-blue"
                />
                <span className="ml-3 text-gray-900">{option}</span>
              </label>
            ))}
          </div>
        )}

        {currentQuestion.type === 'short_answer' && (
          <input
            type="text"
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            disabled={hasAnsweredCurrent}
            placeholder="Type your answer here..."
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-cte-blue focus:ring-2 focus:ring-cte-blue focus:ring-opacity-20 disabled:opacity-60 disabled:bg-gray-50"
          />
        )}
      </div>

      {/* Feedback */}
      {feedback.isCorrect !== undefined && (
        <div
          className={`mb-6 p-4 rounded-lg border-2 ${
            feedback.isCorrect
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}
        >
          <div className="flex items-start space-x-3">
            {feedback.isCorrect ? (
              <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0" />
            ) : (
              <XCircleIcon className="w-6 h-6 text-red-600 flex-shrink-0" />
            )}
            <div className="flex-1">
              <p className={`font-semibold mb-1 ${
                feedback.isCorrect ? 'text-green-900' : 'text-red-900'
              }`}>
                {feedback.isCorrect ? 'Correct!' : 'Incorrect'}
              </p>
              {!feedback.isCorrect && feedback.correctAnswer && (
                <p className="text-sm text-gray-700 mb-2">
                  The correct answer is: <span className="font-semibold">{feedback.correctAnswer}</span>
                </p>
              )}
              {feedback.explanation && (
                <p className="text-sm text-gray-700">
                  {feedback.explanation}
                </p>
              )}
            </div>
            <div className="text-sm font-semibold">
              +{feedback.pointsEarned}
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {Object.keys(answers).length} of {questions.length} answered
        </div>
        
        <div className="flex space-x-3">
          {!hasAnsweredCurrent ? (
            <button
              onClick={submitAnswer}
              disabled={!userAnswer.trim() || submittingAnswer}
              className="px-6 py-2 bg-cte-blue text-white font-semibold rounded-lg hover:bg-cte-blue-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submittingAnswer ? 'Submitting...' : 'Submit Answer'}
            </button>
          ) : (
            <>
              {!isLastQuestion ? (
                <button
                  onClick={nextQuestion}
                  className="px-6 py-2 bg-cte-blue text-white font-semibold rounded-lg hover:bg-cte-blue-dark transition-colors"
                >
                  Next Question →
                </button>
              ) : (
                <button
                  onClick={completeQuiz}
                  className="px-6 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
                >
                  Complete Quiz
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
